import { QuestionVersionDiff } from '@/components/question-version-diff';
import { useCreateMessage, useCreateQuestionVersion, useQuestion } from '@/hooks/useQuestions';
import { extractTextFromOptionJson, extractTextFromTitleJson } from '@/lib/lexical-utils';
import type { AppContext } from '@/main';
import { createHeadlessEditor } from '@lexical/headless';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { $createQuestionOptionNode, nodes as editorNodes } from '@ui/components';
import { SerializedQuestionOptionNode } from '@ui/components/';
import { Editor } from '@ui/components/blocks/editor-x/editor';
import { Button } from '@ui/components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@ui/components/dropdown-menu';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@ui/components/hover-card';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupTextarea,
} from '@ui/components/input-group';
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@ui/components/resizable';
import { ScrollArea } from '@ui/components/scroll-area';
import { Separator } from '@ui/components/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@ui/components/tabs';
import { Toggle } from '@ui/components/toggle';
import { formatDistanceToNow } from 'date-fns';
import type { SerializedEditorState, SerializedLexicalNode } from 'lexical';
import { $createParagraphNode, $createTextNode } from 'lexical';
import {
  ArchiveRestore,
  ClockFading,
  Copy,
  Eye,
  EyeOff,
  Hash,
  Info,
  Lock,
  Save,
  SendHorizontal,
  Settings,
  Shield,
  Sparkles,
  Tag,
} from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

export const Route = createFileRoute('/_dashboard/questions/$questionId')<AppContext>({
  component: RouteComponent,
  loader: () => ({
    crumb: 'Question Details',
  }),
});

const defaultEditorState: SerializedEditorState = {
  root: {
    type: 'root',
    version: 1,
    children: [
      {
        type: 'paragraph',
        version: 1,
      },
    ],
    direction: 'ltr',
    format: 'left',
    indent: 0,
  },
};

interface AIMessageCardProps {
  response: string;
  model: string;
  createdAt: Date;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  onReplace: (response: string) => void;
}

function AIMessageCard({
  response,
  model,
  createdAt,
  inputTokens,
  outputTokens,
  totalTokens,
  onReplace,
}: AIMessageCardProps) {
  let parsedResponse: {
    title?: string;
    text?: string;
    options?: Array<{ body: string; isCorrect?: boolean }>;
    correctAnswer?: number;
  } | null = null;

  try {
    parsedResponse = JSON.parse(response);
  } catch (error) {
    // Response is not JSON, show as plain text
  }

  const handleReplace = () => {
    onReplace(response);
  };

  return (
    <div className="text-left border p-2">
      <div className="flex items-center justify-between mb-1">
        <div className="text-xs text-muted-foreground">
          {model} • {formatDistanceToNow(createdAt, { addSuffix: true })}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleReplace}
          className="h-6 px-2 text-xs hover:bg-primary hover:text-primary-foreground"
        >
          <ArchiveRestore className="h-3 w-3 mr-1" />
          Replace
        </Button>
      </div>

      {parsedResponse && parsedResponse.title && parsedResponse.text ? (
        <div className="space-y-2">
          {/* Title */}
          <div className="space-y-1">
            <div className="text-xs font-semibold text-muted-foreground flex items-center gap-1">
              <div className="w-0.5 h-3 bg-primary rounded-full" />
              Question Title
            </div>
            <div className="p-2 rounded bg-secondary-foreground/10 text-sm">
              {`${parsedResponse.title}\n\n\n${parsedResponse.text}` || ''}
            </div>
          </div>

          {/* Options */}
          {parsedResponse.options && parsedResponse.options.length > 0 && (
            <div className="space-y-1">
              <div className="text-xs font-semibold text-muted-foreground flex items-center gap-1">
                <div className="w-0.5 h-3 bg-blue-500 rounded-full" />
                Options ({parsedResponse.options.length})
              </div>
              <div className="space-y-1">
                {parsedResponse.options.map((opt, idx) => {
                  return (
                    <div
                      key={idx}
                      className={`p-2 rounded text-sm border ${
                        opt.isCorrect
                          ? 'bg-green-500/5 border-green-500/20'
                          : 'bg-muted/30 border-muted'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-muted-foreground text-xs">
                          {idx + 1}.
                        </span>
                        <span className="flex-1">{opt.body}</span>
                        {opt.isCorrect && (
                          <span className="px-1.5 py-0.5 rounded bg-green-500/20 text-green-700 dark:text-green-400 font-medium text-xs">
                            ✓
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Correct Answer */}
          {parsedResponse.correctAnswer !== undefined && (
            <div className="text-xs text-muted-foreground">
              Correct Answer: {parsedResponse.correctAnswer + 1}
            </div>
          )}
        </div>
      ) : (
        <div className="p-2 rounded bg-secondary-foreground/10 text-sm whitespace-pre-wrap">
          error
        </div>
      )}

      <div className="text-xs text-muted-foreground mt-1">
        Tokens: {totalTokens} (in: {inputTokens}, out: {outputTokens})
      </div>
    </div>
  );
}

/**
 * Calculate detailed diff between two text strings
 */
function calculateTextDiff(
  oldText: string,
  newText: string
): Array<{ type: 'added' | 'removed' | 'unchanged'; text: string }> {
  // Simple word-based diff
  const oldWords = oldText.split(/(\s+)/);
  const newWords = newText.split(/(\s+)/);

  const result: Array<{ type: 'added' | 'removed' | 'unchanged'; text: string }> = [];

  let i = 0,
    j = 0;
  while (i < oldWords.length || j < newWords.length) {
    if (i >= oldWords.length) {
      // Only new words left
      result.push({ type: 'added', text: newWords[j] });
      j++;
    } else if (j >= newWords.length) {
      // Only old words left
      result.push({ type: 'removed', text: oldWords[i] });
      i++;
    } else if (oldWords[i] === newWords[j]) {
      // Words match
      result.push({ type: 'unchanged', text: oldWords[i] });
      i++;
      j++;
    } else {
      // Words differ - check if it's a replacement or addition/deletion
      const oldWord = oldWords[i];
      const newWord = newWords[j];

      // Check if the new word appears later in old (deletion)
      const newInOldIndex = oldWords.slice(i).indexOf(newWord);
      // Check if the old word appears later in new (addition)
      const oldInNewIndex = newWords.slice(j).indexOf(oldWord);

      if (newInOldIndex !== -1 && (oldInNewIndex === -1 || newInOldIndex < oldInNewIndex)) {
        // Deletion
        result.push({ type: 'removed', text: oldWord });
        i++;
      } else if (oldInNewIndex !== -1) {
        // Addition
        result.push({ type: 'added', text: newWord });
        j++;
      } else {
        // Replacement
        result.push({ type: 'removed', text: oldWord });
        result.push({ type: 'added', text: newWord });
        i++;
        j++;
      }
    }
  }

  return result;
}

/**
 * Calculate diff between two versions
 */
function getVersionDiff(currentVersion: any, previousVersion: any | null) {
  if (!previousVersion) {
    return {
      type: 'created',
      hasChanges: true,
      titleDiff: null,
      optionDiffs: [],
      metadata: {
        typeChanged: false,
      },
    };
  }

  // Check title changes
  const currentTitle = extractTextFromTitleJson(currentVersion.title);
  const prevTitle = extractTextFromTitleJson(previousVersion.title);
  const titleDiff = currentTitle !== prevTitle ? calculateTextDiff(prevTitle, currentTitle) : null;

  // Check options changes
  const currentOptions = currentVersion.options || [];
  const prevOptions = previousVersion.options || [];

  const optionDiffs: Array<{
    type: 'added' | 'removed' | 'modified' | 'unchanged';
    index: number;
    diff: ReturnType<typeof calculateTextDiff> | null;
    isCorrectChanged: boolean;
    oldIsCorrect?: boolean;
    newIsCorrect?: boolean;
    text?: string;
  }> = [];

  const maxLength = Math.max(currentOptions.length, prevOptions.length);

  for (let i = 0; i < maxLength; i++) {
    const currentOpt = currentOptions[i];
    const prevOpt = prevOptions[i];

    if (!prevOpt && currentOpt) {
      // Added option
      optionDiffs.push({
        type: 'added',
        index: i,
        diff: null,
        isCorrectChanged: false,
        text: extractTextFromOptionJson(currentOpt.body),
        newIsCorrect: currentOpt.isCorrect,
      });
    } else if (prevOpt && !currentOpt) {
      // Removed option
      optionDiffs.push({
        type: 'removed',
        index: i,
        diff: null,
        isCorrectChanged: false,
        text: extractTextFromOptionJson(prevOpt.body),
        oldIsCorrect: prevOpt.isCorrect,
      });
    } else if (prevOpt && currentOpt) {
      // Check if modified
      const currText = extractTextFromOptionJson(currentOpt.body);
      const prevText = extractTextFromOptionJson(prevOpt.body);
      const textChanged = currText !== prevText;
      const isCorrectChanged = currentOpt.isCorrect !== prevOpt.isCorrect;

      if (textChanged || isCorrectChanged) {
        optionDiffs.push({
          type: 'modified',
          index: i,
          diff: textChanged ? calculateTextDiff(prevText, currText) : null,
          isCorrectChanged,
          oldIsCorrect: prevOpt.isCorrect,
          newIsCorrect: currentOpt.isCorrect,
          text: currText, // Include current text as fallback
        });
      }
    }
  }

  // Check type change
  const typeChanged = currentVersion.type !== previousVersion.type;

  const hasChanges = titleDiff !== null || optionDiffs.length > 0 || typeChanged;

  return {
    type: hasChanges ? 'modified' : 'no-change',
    hasChanges,
    titleDiff,
    optionDiffs,
    metadata: {
      typeChanged,
      oldType: previousVersion.type,
      newType: currentVersion.type,
    },
  };
}

function RouteComponent() {
  const { questionId } = Route.useParams();
  const createQuestionVersionMutation = useCreateQuestionVersion();
  const queryClient = useQueryClient();

  const [editorState, setEditorState] = useState<SerializedEditorState>(defaultEditorState);
  const [lastCursor, setLastCursor] = useState<string | undefined>(undefined);

  const { data: question, isLoading, error, refetch } = useQuestion(questionId, lastCursor);

  const parsedEditorState = useMemo<SerializedEditorState>(() => {
    if (!question?.activeVersion) return defaultEditorState;

    const parsed = JSON.parse(question.activeVersion.title);
    if (!parsed || !parsed.root) {
      return defaultEditorState;
    }

    const options: SerializedLexicalNode[] = [];
    question.activeVersion.options.forEach((option) => {
      options.push(JSON.parse(option.body) as SerializedLexicalNode);
    });

    return {
      root: {
        ...parsed.root,
        children: [...parsed.root.children, ...options],
      },
    };
  }, [question?.activeVersion]);

  const handleSave = useCallback(async () => {
    if (!question) return;

    const questionOptions: SerializedQuestionOptionNode[] = [];
    const indexesToRemove: number[] = [];
    editorState.root.children.forEach((node, index) => {
      if (node.type === 'question-option') {
        questionOptions.push(node as SerializedQuestionOptionNode);
        indexesToRemove.push(index);
      }
    });

    await createQuestionVersionMutation.mutateAsync({
      questionId: question.id,
      data: {
        title: JSON.stringify({
          ...editorState,
          root: {
            ...editorState.root,
            children: editorState.root.children.filter(
              (_, index) => !indexesToRemove.includes(index)
            ),
          },
        }),
        type: question.activeVersion?.type || 'multiple-choice',
        options: questionOptions.map((option) => ({
          body: JSON.stringify(option),
          isCorrect: option.isCorrect,
        })),
        correctAnswer: 0,
        strict: question.activeVersion?.strict ?? false,
      },
    });

    await refetch();

    await queryClient.invalidateQueries({ queryKey: ['question', questionId] });
    await queryClient.invalidateQueries({ queryKey: ['questions'] });
  }, [question, editorState, createQuestionVersionMutation, refetch, queryClient, questionId]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading question...</div>
      </div>
    );
  }

  if (error || !question) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <div className="text-destructive">Failed to load question</div>
        <Button variant="outline" onClick={() => window.location.reload()}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <ResizablePanelGroup direction="horizontal">
      <ResizablePanel className="flex h-full w-2/3 flex-col" defaultSize={60} minSize={30}>
        <div className="flex shrink-0 justify-between p-1 pb-0 w-full">
          <h1 className="text-xl font-bold tracking-tight">&nbsp;</h1>
          <div className="flex gap-2">
            <HoverCard>
              <HoverCardTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                  <Info className="h-4 w-4" />
                </Button>
              </HoverCardTrigger>
              <HoverCardContent className="min-w-fit">
                <div className="space-y-3">
                  <div className="space-y-3">
                    <h4 className="text-sm font-semibold">Question Information</h4>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm">
                        <Hash className="h-4 w-4 text-muted-foreground" />
                        <span className="text-muted-foreground">Question ID:</span>
                        <button
                          onClick={() => navigator.clipboard.writeText(question.id)}
                          className="font-mono text-xs hover:text-primary cursor-pointer flex items-center gap-1"
                          title="Click to copy"
                        >
                          {question.id}
                          <Copy className="h-3 w-3 opacity-0 hover:opacity-100 transition-opacity" />
                        </button>
                      </div>

                      <div className="flex items-center gap-2 text-sm">
                        <Tag className="h-4 w-4 text-muted-foreground" />
                        <span className="text-muted-foreground">Public ID:</span>
                        <button
                          onClick={() => navigator.clipboard.writeText(question.publicId)}
                          className="font-mono text-xs hover:text-primary cursor-pointer flex items-center gap-1"
                          title="Click to copy"
                        >
                          {question.publicId}
                          <Copy className="h-3 w-3 opacity-0 hover:opacity-100 transition-opacity" />
                        </button>
                      </div>

                      {question.activeVersion && (
                        <div className="flex items-center gap-2 text-sm">
                          <Tag className="h-4 w-4 text-muted-foreground" />
                          <span className="text-muted-foreground">Type:</span>
                          <span className="capitalize">
                            {question.activeVersion.type.replace('-', ' ')}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </HoverCardContent>
            </HoverCard>

            <Toggle pressed={!question.archived} size="sm" aria-label="Status">
              {question.archived ? 'Archived' : 'Active'}
            </Toggle>

            <Toggle pressed={question.public} size="sm" aria-label="Visibility">
              {question.public ? (
                <>
                  <Eye className="h-3 w-3 mr-1" />
                  Public
                </>
              ) : (
                <>
                  <EyeOff className="h-3 w-3 mr-1" />
                  Private
                </>
              )}
            </Toggle>

            {question.activeVersion && (
              <Toggle pressed={question.activeVersion.strict} size="sm" aria-label="Mode">
                {question.activeVersion.strict ? (
                  <>
                    <Lock className="h-3 w-3 mr-1" />
                    Strict
                  </>
                ) : (
                  <>
                    <Shield className="h-3 w-3 mr-1" />
                    Normal
                  </>
                )}
              </Toggle>
            )}
            <Button
              size="sm"
              onClick={handleSave}
              disabled={createQuestionVersionMutation.isPending}
            >
              <Save className="h-4 w-4 mr-2" />
              {createQuestionVersionMutation.isPending ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
        <Separator />

        <div className="min-h-0 flex-1">
          <Editor
            key={`editor-viewing`}
            editorSerializedState={parsedEditorState}
            onSerializedChange={setEditorState}
          />
        </div>
      </ResizablePanel>
      <ResizableHandle />
      <ResizablePanel minSize={15}>
        <Tabs defaultValue="chat" className="flex flex-col w-full h-full gap-0">
          <TabsList className="w-full shrink-0">
            <TabsTrigger value="chat" className="group">
              <Sparkles className="h-4 w-4 group-data-[state=active]:text-primary" />
              AI Chat
            </TabsTrigger>
            <TabsTrigger value="history">
              <ClockFading className="h-4 w-4" />
              History
            </TabsTrigger>
          </TabsList>

          <TabsContent value="chat" className="w-full flex-1 min-h-0">
            <AIChat />
          </TabsContent>
          <TabsContent value="history" className="w-full flex-1 min-h-0">
            <ScrollArea className="h-full w-full">
              {question.versions && question.versions.length > 1 ? (
                <div className="flex flex-col gap-2 w-full p-2">
                  {question.versions
                    .filter((version) => version.id !== question.activeVersion?.id)
                    .map((version) => {
                      // Find the actual index in the original array
                      const originalIndex = question.versions.findIndex((v) => v.id === version.id);

                      // Calculate correct version number
                      const versionNumber = question.versions.length - originalIndex;

                      // Get the next version (older) for comparison
                      // In the array, versions are sorted newest first, so next index is the older one
                      const previousVersion =
                        originalIndex < question.versions.length - 1
                          ? question.versions[originalIndex + 1]
                          : null;

                      // Calculate diff
                      const diff = getVersionDiff(version, previousVersion);

                      return (
                        <QuestionVersionDiff
                          key={version.id}
                          version={version}
                          versionNumber={versionNumber}
                          diff={diff}
                          onRestore={() => setLastCursor(version.id)}
                          onCopyId={() => navigator.clipboard.writeText(version.id)}
                        />
                      );
                    })}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-center p-4">
                  <ClockFading className="h-12 w-12 text-muted-foreground/50 mb-3" />
                  <div className="text-sm text-muted-foreground mb-2">
                    No previous versions available
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Create new versions by editing and saving the question
                  </div>
                </div>
              )}
            </ScrollArea>
          </TabsContent>
        </Tabs>
      </ResizablePanel>
    </ResizablePanelGroup>
  );
}

function AIChat() {
  const { questionId } = Route.useParams();
  const { data: question } = useQuestion(questionId);
  const [selectedModel, setSelectedModel] = useState<string>('gpt-4o');
  const createMessageMutation = useCreateMessage();
  const createQuestionVersionMutation = useCreateQuestionVersion();
  const queryClient = useQueryClient();

  const messages = question?.messages || [];

  const modelLabels: Record<string, string> = {
    'gpt-5.1': 'GPT-5.1',
    'gpt-4.1': 'GPT-4.1',
    'gpt-4o': 'GPT-4o',
    'gpt-4o-mini': 'GPT-4o Mini',
    'o1-mini': 'o1 Mini',
    'o3-mini': 'o3 Mini',
  };

  const handleReplaceContent = (response: string) => {
    try {
      const parsedResponse = JSON.parse(response);

      if (parsedResponse.title && parsedResponse.options) {
        const headlessEditor = createHeadlessEditor({
          nodes: editorNodes,
          onError: (error) => {
            console.error('Headless editor error:', error);
          },
        });

        let titleJSON: string = '';
        let optionsJSON: { body: string; isCorrect?: boolean }[] = [];

        headlessEditor.update(() => {
          const title = $createParagraphNode().exportJSON();
          title.children = [$createTextNode(parsedResponse.title).exportJSON()];

          const text = $createParagraphNode().exportJSON();
          text.children = [$createTextNode(parsedResponse.text).exportJSON()];

          titleJSON = JSON.stringify({
            root: {
              children: [title, text],
              direction: 'ltr',
              format: 'left',
              indent: 0,
              type: 'root',
              version: 1,
            },
          });
          optionsJSON = parsedResponse.options.map(
            (option: { body: string; isCorrect: boolean }, index: number) => {
              const optionNode = $createQuestionOptionNode(index, option.isCorrect).exportJSON();
              optionNode.children = [$createTextNode(option.body).exportJSON()];
              return { body: JSON.stringify(optionNode), isCorrect: option.isCorrect };
            }
          );
        });

        createQuestionVersionMutation.mutate(
          {
            questionId,
            data: {
              title: titleJSON,
              type: parsedResponse.type || question?.activeVersion?.type || 'multiple-choice',
              options: optionsJSON,
              correctAnswer: parsedResponse.correctAnswer,
              strict: question?.activeVersion?.strict ?? false,
            },
          },
          {
            onSuccess: () => {
              queryClient.invalidateQueries({ queryKey: ['question', questionId] });
            },
          }
        );
      }
    } catch (error) {
      console.error('Failed to replace content:', error);
    }
  };

  return (
    <div className="w-full h-full relative flex flex-col justify-end-safe">
      <div className="overflow-y-auto flex-1">
        {messages.length === 0 && !createMessageMutation.isPending ? (
          <div className="flex flex-col items-center justify-center h-full text-center p-4">
            <div className="text-sm text-muted-foreground mb-2">
              Chat with AI using {modelLabels[selectedModel]}
            </div>
            <div className="text-xs text-muted-foreground">
              Ask questions about the exam or get help with content
            </div>
          </div>
        ) : (
          <ScrollArea className="h-full">
            <div className="flex flex-col gap-2">
              {messages.map((msg) => (
                <div className="flex flex-col gap-2">
                  <div key={msg.id} className="flex flex-col justify-center items-center px-2">
                    <div className="text-left border w-full p-2">
                      <div className="text-xs text-muted-foreground mb-1">
                        User • {formatDistanceToNow(new Date(msg.createdAt), { addSuffix: true })}
                      </div>
                      <div className="p-2 rounded bg-secondary-foreground/10 text-sm">
                        {msg.userPrompt}
                      </div>
                    </div>
                    <Separator orientation="vertical" className="h-6!" />
                    <AIMessageCard
                      response={msg.response}
                      model={msg.model}
                      createdAt={new Date(msg.createdAt)}
                      inputTokens={msg.inputTokens}
                      outputTokens={msg.outputTokens}
                      totalTokens={msg.totalTokens}
                      onReplace={handleReplaceContent}
                    />
                  </div>
                  <Separator orientation="horizontal" className="bg-secondary" />
                </div>
              ))}
              {createMessageMutation.isPending && (
                <div className="mb-2 text-left">
                  <div className="inline-block py-0 px-2 rounded bg-secondary-foreground text-secondary">
                    <span className="animate-pulse">Generating response...</span>
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>
        )}
      </div>
      <Separator />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const form = e.target as HTMLFormElement;
          const input = form.elements.namedItem('message') as HTMLInputElement;
          const userMessage = input.value;
          if (!userMessage.trim()) return;

          input.value = '';

          // Call the mutation to generate AI response
          createMessageMutation.mutate({
            questionId,
            data: {
              prompt: userMessage,
              messageType: 'generate',
              type: question?.activeVersion?.type || 'multiple-choice',
              model: selectedModel,
            },
          });
        }}
      >
        <InputGroup className="flex-col items-stretch border-0">
          <InputGroupTextarea
            name="message"
            placeholder="Type your message here..."
            className="min-h-[60px]"
            disabled={createMessageMutation.isPending}
          />
          <InputGroupAddon align="block-end" className="justify-between">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <InputGroupButton
                  type="button"
                  size="xs"
                  className="gap-1"
                  disabled={createMessageMutation.isPending}
                >
                  {modelLabels[selectedModel]}
                  <Settings className="h-3 w-3" />
                </InputGroupButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-72">
                <DropdownMenuLabel>AI Model Selection</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuRadioGroup value={selectedModel} onValueChange={setSelectedModel}>
                  <DropdownMenuRadioItem value="gpt-5.1" className="flex-col items-start py-3">
                    <div className="font-medium">GPT-5.1</div>
                    <div className="text-xs text-muted-foreground">
                      Latest generation flagship model
                    </div>
                  </DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="gpt-4.1" className="flex-col items-start py-3">
                    <div className="font-medium">GPT-4.1</div>
                    <div className="text-xs text-muted-foreground">
                      Enhanced GPT-4 with improved capabilities
                    </div>
                  </DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="gpt-4o" className="flex-col items-start py-3">
                    <div className="font-medium">GPT-4o</div>
                    <div className="text-xs text-muted-foreground">
                      Most capable, best for complex tasks
                    </div>
                  </DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="gpt-4o-mini" className="flex-col items-start py-3">
                    <div className="font-medium">GPT-4o Mini</div>
                    <div className="text-xs text-muted-foreground">Faster and more affordable</div>
                  </DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="o1-mini" className="flex-col items-start py-3">
                    <div className="font-medium">o1 Mini</div>
                    <div className="text-xs text-muted-foreground">
                      Advanced reasoning for coding and math
                    </div>
                  </DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="o3-mini" className="flex-col items-start py-3">
                    <div className="font-medium">o3 Mini</div>
                    <div className="text-xs text-muted-foreground">Next-gen reasoning model</div>
                  </DropdownMenuRadioItem>
                </DropdownMenuRadioGroup>
              </DropdownMenuContent>
            </DropdownMenu>
            <InputGroupButton type="submit" size="xs" disabled={createMessageMutation.isPending}>
              <SendHorizontal className="h-3 w-3" />
              {createMessageMutation.isPending ? 'Sending...' : 'Send'}
            </InputGroupButton>
          </InputGroupAddon>
        </InputGroup>
      </form>
    </div>
  );
}
